import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Accordion,
    AccordionItem,
    useToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import PromoCodeFields from '../forms/promo-code-fields'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {pxToRem} from '../../utills/pxToRem'
import {extractCouponsFromBasket} from '../../utills/couponUtils'

export const usePromoCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()
    const toast = useToast()

    const applyPromoCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removePromoCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')

    const submitPromoCode = async ({code}) => {
        try {
            const currentCoupons = extractCouponsFromBasket(basket)
            const existingCoupon = currentCoupons.find(
                (coupon) => coupon.couponCode.toLowerCase() === code.toLowerCase()
            )

            if (existingCoupon && existingCoupon.isApplied) {
                form.setError(
                    'code',
                    {
                        type: 'manual',
                        message: formatMessage({
                            defaultMessage: 'This promo code is already applied',
                            id: 'use_promocode.error.already_applied'
                        })
                    },
                    {shouldFocus: true}
                )
                return
            }

            const updatedBasket = await applyPromoCodeMutation.mutateAsync({
                parameters: {basketId: basket?.basketId},
                body: {
                    code
                }
            })

            form.reset(
                {code: ''},
                {
                    keepErrors: false,
                    keepDirty: false,
                    keepIsSubmitSuccessful: false
                }
            )

            const allCoupons = extractCouponsFromBasket(updatedBasket)
            const appliedCoupon = allCoupons.find(
                (coupon) => coupon.couponCode.toLowerCase() === code.toLowerCase()
            )

            if (!appliedCoupon || !appliedCoupon.isApplied) {
                const errorMessage = appliedCoupon?.errorMessage || `Coupon code ${code} is unknown`

                form.setError(
                    'code',
                    {
                        type: 'manual',
                        message: errorMessage
                    },
                    {shouldFocus: true}
                )
            } else {
                toast({
                    title: formatMessage({
                        defaultMessage: 'Promotion applied',
                        id: 'use_promocode.info.promo_applied'
                    }),
                    status: 'success',
                    position: 'top-right',
                    isClosable: true
                })
            }
        } catch (e) {
            form.setError(
                'code',
                {
                    type: 'manual',
                    message: `Coupon code ${code} is unknown`
                },
                {shouldFocus: true}
            )
        }
    }

    const removePromoCode = async (couponItemId) => {
        removePromoCodeMutation.mutate(
            {
                parameters: {basketId: basket?.basketId, couponItemId}
            },
            {
                onSuccess: () => {
                    // Clear promo code errors if they exist
                    if (form?.formState?.errors?.code) {
                        form.clearErrors('code')
                    }
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Promotion removed',
                            id: 'use_promocode.info.promo_removed'
                        }),
                        status: 'success',
                        position: 'top-right',
                        isClosable: true
                    })
                },
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        position: 'top-right',
                        isClosable: true
                    })
                }
            }
        )
    }

    return {form, submitPromoCode, removePromoCode}
}

export const PromoCode = ({form, submitPromoCode, removePromoCode, itemProps}) => {
    const [isOpen, setOpen] = useState()
    const {data: basket} = useCurrentBasket()

    useEffect(() => {
        if (form.formState.isSubmitSuccessful) {
            setOpen(false)
        }
    }, [form.formState.isSubmitSuccessful])

    return (
        <Accordion allowToggle index={isOpen ? 0 : -1} onChange={() => setOpen(!isOpen)}>
            <AccordionItem {...itemProps}>
                <>
                    <Box
                        data-testid="promo-code-form"
                        as="form"
                        onSubmit={form.handleSubmit(submitPromoCode)}
                    >
                        <PromoCodeFields
                            form={form}
                            marginBottom={pxToRem(30)}
                            basket={basket}
                            removePromoCode={removePromoCode}
                        />
                    </Box>
                </>
            </AccordionItem>
        </Accordion>
    )
}

PromoCode.propTypes = {
    /** The form object returned from `usePromoCode` hook */
    form: PropTypes.object.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    submitPromoCode: PropTypes.func.isRequired,

    /** The remove promo code callback returned from `usePromoCode` hook */
    removePromoCode: PropTypes.func.isRequired,

    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object
}

export default PromoCode
